import { useState } from 'react';
import { Trans } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { tokens } from '@actual-app/components/tokens';
import { View } from '@actual-app/components/view';

import type { ReadyFinancialDashboard } from '#components/product-finance/client';
import { formatProductDate } from '#components/product-finance/money';

import { CommitmentRow, PaidCommitmentRow } from './CommitmentsSection';
import { CycleCard, NextIncomeCard } from './CurrentCycleCard';
import {
  EmptySection,
  occurrenceTarget,
  SectionCard,
} from './dashboard-shared';
import type { ActiveCommand } from './FinancialDashboardPage';
import { GoalsCard } from './GoalsSection';
import { PaymentOutlookCard } from './PaymentOutlookCard';
import { RecentFactsCard } from './RecentMovements';
import { SafeToSpendHero } from './SafeToSpendCard';

export function FinancialDashboard({
  dashboard,
  onOpenCommand,
  onEditSettings,
  onDeleteMovement,
  onUndoReconciliation,
  onSetGoalContribution,
  onRecordGoalProgress,
  onDeleteGoalProgressEntry,
}: {
  dashboard: ReadyFinancialDashboard;
  onOpenCommand: (command: ActiveCommand) => void;
  onEditSettings: () => void;
  onDeleteMovement: (
    actualFactId: string,
    options?: { removeLinkedContributions?: boolean },
  ) => Promise<void>;
  onUndoReconciliation: (
    occurrenceKey: string,
    options?: { removeLinkedContributions?: boolean },
  ) => Promise<void>;
  onSetGoalContribution: (
    goalId: string,
    amountMinorUnits: number,
  ) => Promise<void>;
  onRecordGoalProgress: (input: {
    goalId: string;
    kind: 'contribution' | 'withdrawal';
    amountMinorUnits: number;
    date: string;
    note?: string;
    commandId?: string;
  }) => Promise<void>;
  onDeleteGoalProgressEntry: (goalId: string, entryId: string) => Promise<void>;
}) {
  const [isSafeDetailOpen, setSafeDetailOpen] = useState(false);
  const currentCommitments = dashboard.pendingCurrentCycleCommitments.filter(
    item => item.temporalState !== 'overdue',
  );
  const groupedCommitmentIds = new Set(
    [
      ...dashboard.pendingCurrentCycleCommitments,
      ...dashboard.futureCycleCommitments,
    ].map(item => item.occurrence.id),
  );
  const otherUpcoming = dashboard.upcomingCommitments.filter(
    item => !groupedCommitmentIds.has(item.occurrence.id),
  );
  const nextExpectedIncome =
    dashboard.missedIncomes[0]?.occurrence ??
    dashboard.pendingIncomes[0]?.occurrence;
  const nextFixedPayment =
    dashboard.overdueCommitments[0] ??
    currentCommitments[0] ??
    dashboard.futureCycleCommitments[0] ??
    otherUpcoming[0];

  return (
    <View style={{ flexShrink: 0, gap: 14 }}>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'flex-end',
        }}
      >
        <Button onPress={onEditSettings} style={{ minHeight: 42 }}>
          <Trans>Financial settings</Trans>
        </Button>
      </View>

      <SectionCard title={<Trans>Quick capture</Trans>}>
        <View
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 8,
            [`@media (max-width: ${tokens.breakpoint_small})`]: {
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            },
          }}
        >
          <Button
            variant="primary"
            onPress={() => onOpenCommand({ kind: 'expense' })}
            style={{ minHeight: 44 }}
          >
            <Trans>Record expense</Trans>
          </Button>
          <Button
            isDisabled={nextExpectedIncome === undefined}
            onPress={() => {
              if (nextExpectedIncome !== undefined) {
                onOpenCommand({
                  kind: 'expected-income',
                  target: occurrenceTarget(nextExpectedIncome),
                });
              }
            }}
            style={{ minHeight: 44 }}
          >
            <Trans>Register income</Trans>
          </Button>
          <Button
            onPress={() => onOpenCommand({ kind: 'extra-income' })}
            style={{ minHeight: 44 }}
          >
            <Trans>Record extra income</Trans>
          </Button>
          <Button
            isDisabled={nextFixedPayment === undefined}
            onPress={() => {
              if (nextFixedPayment !== undefined) {
                onOpenCommand({
                  kind: 'scheduled-payment',
                  target: occurrenceTarget(nextFixedPayment.occurrence),
                });
              }
            }}
            style={{ minHeight: 44 }}
          >
            <Trans>Record fixed expense payment</Trans>
          </Button>
        </View>
      </SectionCard>

      <SafeToSpendHero
        dashboard={dashboard}
        isDetailOpen={isSafeDetailOpen}
        onToggleDetail={() => setSafeDetailOpen(open => !open)}
      />

      <View
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 14,
          [`@media (max-width: ${tokens.breakpoint_small})`]: {
            gridTemplateColumns: 'minmax(0, 1fr)',
          },
        }}
      >
        <CycleCard dashboard={dashboard} />
        <NextIncomeCard dashboard={dashboard} onOpenCommand={onOpenCommand} />
      </View>

      <PaymentOutlookCard dashboard={dashboard} />

      {dashboard.overdueCommitments.length > 0 && (
        <SectionCard title={<Trans>Overdue payments</Trans>} tone="overdue">
          {dashboard.overdueCommitments.map(item => (
            <CommitmentRow
              key={item.occurrence.id}
              item={item}
              badge={<Trans>Overdue</Trans>}
              onRecord={() =>
                onOpenCommand({
                  kind: 'scheduled-payment',
                  target: occurrenceTarget(item.occurrence),
                })
              }
            />
          ))}
        </SectionCard>
      )}

      <SectionCard
        title={
          dashboard.currentCycle === null ? (
            <Trans>Current-cycle payments</Trans>
          ) : (
            <Trans>Payments in this cycle</Trans>
          )
        }
      >
        {currentCommitments.length === 0 ? (
          <EmptySection>
            <Trans>No pending payments in the current cycle.</Trans>
          </EmptySection>
        ) : (
          currentCommitments.map(item => (
            <CommitmentRow
              key={item.occurrence.id}
              item={item}
              badge={<Trans>Pending</Trans>}
              onRecord={() =>
                onOpenCommand({
                  kind: 'scheduled-payment',
                  target: occurrenceTarget(item.occurrence),
                })
              }
            />
          ))
        )}
        {dashboard.paidCurrentCycleCommitments.map(item =>
          item.status === 'matched' ? (
            <PaidCommitmentRow
              key={item.occurrence.id}
              item={item}
              onUndo={() => void onUndoReconciliation(item.occurrence.id)}
            />
          ) : null,
        )}
      </SectionCard>

      <SectionCard title={<Trans>Upcoming payments</Trans>}>
        {dashboard.futureCycleCommitments.length === 0 &&
        otherUpcoming.length === 0 ? (
          <EmptySection>
            <Trans>No upcoming commitments.</Trans>
          </EmptySection>
        ) : (
          <>
            {dashboard.futureCycleCommitments.map(item => (
              <CommitmentRow
                key={item.occurrence.id}
                item={item}
                badge={
                  <>
                    {item.budgetCycle.name} ·{' '}
                    {formatProductDate(item.fundingDate)}
                  </>
                }
                onRecord={() =>
                  onOpenCommand({
                    kind: 'scheduled-payment',
                    target: occurrenceTarget(item.occurrence),
                  })
                }
              />
            ))}
            {otherUpcoming.map(item => (
              <CommitmentRow
                key={item.occurrence.id}
                item={item}
                badge={<Trans>Upcoming</Trans>}
                onRecord={() =>
                  onOpenCommand({
                    kind: 'scheduled-payment',
                    target: occurrenceTarget(item.occurrence),
                  })
                }
              />
            ))}
          </>
        )}
      </SectionCard>

      <View
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 14,
          [`@media (max-width: ${tokens.breakpoint_small})`]: {
            gridTemplateColumns: 'minmax(0, 1fr)',
          },
        }}
      >
        <RecentFactsCard
          dashboard={dashboard}
          onEdit={movement =>
            onOpenCommand({
              kind: 'edit-movement',
              target: {
                occurrenceKey: movement.occurrenceKey ?? movement.fact.id,
                actualFactId: movement.fact.id,
                accountId: movement.accountId,
                factType: movement.fact.type,
                name: movement.fact.description ?? '',
                amount: movement.fact.amount,
                date: movement.fact.date,
              },
            })
          }
          onDelete={onDeleteMovement}
          onUndo={onUndoReconciliation}
        />
        <GoalsCard
          dashboard={dashboard}
          onSetContribution={onSetGoalContribution}
          onRecordProgress={onRecordGoalProgress}
          onDeleteProgressEntry={onDeleteGoalProgressEntry}
          onUseEmergencyFund={(goalId, availableMinorUnits) =>
            onOpenCommand({
              kind: 'emergency-expense',
              goalId,
              availableMinorUnits,
            })
          }
        />
      </View>
    </View>
  );
}
